import { Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { AccessRoleDetails } from "../domain/access-role";
import { ApprovalGroupOverview } from "../domain/approval-group";
import { AutomationUserOverview } from "../domain/automation-user";
import { ExtraServiceManageDialogDefinition } from "../domain/extra-service";
import { InvitationDetails } from "../domain/invitation";
import { OrganizationDetails } from "../domain/organization";
import { UserDetails } from "../domain/user";
import { FormPreviewDialogConfig } from "../features/admin/dialogs/form-preview/form-preview-dialog.types";
import { FormPreviewDialog } from "../features/admin/dialogs/form-preview/form-preview.dialog";
import { ApprovalGroupDialogConfig } from "../features/organizations/dialogs/approval-group/approval-group-dialog.types";
import { ApprovalGroupDialog } from "../features/organizations/dialogs/approval-group/approval-group.dialog";
import { DeactivateExtraServiceDialogConfig } from "../features/organizations/dialogs/deactivate-extra-service/deactivate-extra-service-dialog.types";
import { DeactivateExtraServiceDialog } from "../features/organizations/dialogs/deactivate-extra-service/deactivate-extra-service.dialog";
import { DeleteInvitationDialogConfig } from "../features/organizations/dialogs/delete-invitation/delete-invitation-dialog.types";
import { DeleteInvitationDialog } from "../features/organizations/dialogs/delete-invitation/delete-invitation.dialog";
import { EditAccessRoleDialogConfig } from "../features/organizations/dialogs/edit-access-group/edit-access-role-dialog.types";
import { EditAccessRoleDialog } from "../features/organizations/dialogs/edit-access-group/edit-access-role.dialog";
import { EditAutomationUserDialog } from "../features/organizations/dialogs/edit-automation-user/edit-automation-user.dialog";
import {
  EditAutomationDialogMode,
  EditAutomationUserDialogConfig,
} from "../features/organizations/dialogs/edit-automation-user/edit-automation-user.dialog.types";
import { EditUserDialogConfig } from "../features/organizations/dialogs/edit-user/edit-user-dialog.types";
import { EditUserDialog } from "../features/organizations/dialogs/edit-user/edit-user.dialog";
import { NewInvitationDialogConfig } from "../features/organizations/dialogs/new-invitation/new-invitation-dialog.types";
import { NewInvitationDialog } from "../features/organizations/dialogs/new-invitation/new-invitation.dialog";
import { ServiceManageDialog } from "../features/organizations/dialogs/service-manage/service-manage.dialog";
import { TosDialog } from "../features/organizations/dialogs/tos/tos.dialog";
import { TosDialogConfig } from "../features/organizations/dialogs/tos/tos.dialog.types";
import {
  TransactionActionType,
  TransactionApprovalDialogConfig,
} from "../features/organizations/dialogs/transaction-approval/transaction-approval-dialog.types";
import { TransactionApprovalDialog } from "../features/organizations/dialogs/transaction-approval/transaction-approval.dialog";
import { UserForSettingsPage } from "../features/organizations/pages/settings/users/users.component.types";
import {
  PdfPreviewDialog,
  PdfPreviewDialogConfig,
} from "../shared/ui/components/pdf-preview-dialog/pdf-preview.dialog";

@Injectable({
  providedIn: "root",
})
export class ConnectDialogService {
  constructor(
    public dialogService: DialogService,
    public translocoService: TranslocoService,
  ) {}

  public displayNewInvitationDialog(organization: OrganizationDetails): DynamicDialogRef<NewInvitationDialog> {
    const dialogRef = this.dialogService.open<NewInvitationDialog, NewInvitationDialogConfig>(NewInvitationDialog, {
      modal: true,
      draggable: true,
      header: this.translocoService.translate("app.organization-settings.inviteDialog.header"),
      closable: true,
      width: "35vw",
      height: "80vh",
      contentStyle: { "max-height": "80vh" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { organization },
    });

    return dialogRef;
  }

  public displayEditUserDialog(
    user: UserForSettingsPage<UserDetails>,
    organization: OrganizationDetails,
    requestingUserId: number,
    canModifyUsers: boolean,
    canRemoveUsers: boolean,
  ): DynamicDialogRef<EditUserDialog> {
    const dialogRef = this.dialogService.open<EditUserDialog, EditUserDialogConfig>(EditUserDialog, {
      modal: true,
      draggable: true,
      header: this.translocoService.translate("app.organization-settings.editUserDialog.header"),
      closable: true,
      contentStyle: { "overflow-y": "visible" },
      width: "35vw",
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { user, organization, requestingUserId, canModifyUsers, canRemoveUsers },
    });

    return dialogRef;
  }

  public displayEditAutomationUserDialog(
    openingUser: UserDetails,
    organization: OrganizationDetails,
    automationUser: AutomationUserOverview | null,
  ): DynamicDialogRef<EditAutomationUserDialog> {
    const dialogMode = automationUser ? EditAutomationDialogMode.Edit : EditAutomationDialogMode.Create;
    const dialogHeader =
      dialogMode === EditAutomationDialogMode.Create
        ? this.translocoService.translate("app.organization-settings.editAutomationUserDialog.createHeader")
        : this.translocoService.translate("app.organization-settings.editAutomationUserDialog.editHeader");
    const dialogRef = this.dialogService.open<EditAutomationUserDialog, EditAutomationUserDialogConfig>(
      EditAutomationUserDialog,
      {
        modal: true,
        draggable: true,
        header: dialogHeader,
        closable: true,
        contentStyle: { "overflow-y": "visible" },
        width: "35vw",
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: { openingUser, organization, automationUser, dialogMode },
      },
    );

    return dialogRef;
  }

  /**
   * t(app.organization-settings.approvalGroups.editGroup)
   * t(app.organization-settings.approvalGroups.createGroup)
   */
  public displayApprovalGroupDialog(
    orgId: number,
    approvalGroup?: ApprovalGroupOverview,
  ): DynamicDialogRef<ApprovalGroupDialog> {
    const dialogRef = this.dialogService.open<ApprovalGroupDialog, ApprovalGroupDialogConfig>(ApprovalGroupDialog, {
      modal: true,
      draggable: true,
      header: this.translocoService.translate(
        approvalGroup
          ? "app.organization-settings.approvalGroups.editGroup"
          : "app.organization-settings.approvalGroups.createGroup",
      ),
      closable: true,
      contentStyle: { "overflow-y": "visible" },
      width: "35vw",
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { orgId, approvalGroup },
    });

    return dialogRef;
  }

  public displayDeleteInvitationDialog(invitation: InvitationDetails): DynamicDialogRef<DeleteInvitationDialog> {
    const dialogRef = this.dialogService.open<DeleteInvitationDialog, DeleteInvitationDialogConfig>(
      DeleteInvitationDialog,
      {
        modal: true,
        draggable: true,
        header: this.translocoService.translate("app.organization-settings.invitations.deleteInvitation"),
        closable: true,
        width: "30vw",
        breakpoints: {
          "960px": "60vw",
          "640px": "90vw",
        },
        data: { invitation },
      },
    );

    return dialogRef;
  }

  public displayNewAccessRoleDialog(organization: OrganizationDetails): DynamicDialogRef<EditAccessRoleDialog> {
    const dialogRef = this.dialogService.open<EditAccessRoleDialog, EditAccessRoleDialogConfig>(EditAccessRoleDialog, {
      modal: true,
      draggable: true,
      header: this.translocoService.translate("app.organization-settings.editAccessRoleDialog.createHeader"),
      closable: true,
      width: "40vw",
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { accessRole: null, organization },
    });

    return dialogRef;
  }

  public displayEditAccessRoleDialog(
    accessRole: AccessRoleDetails,
    organization: OrganizationDetails,
  ): DynamicDialogRef<EditAccessRoleDialog> {
    const dialogRef = this.dialogService.open<EditAccessRoleDialog, EditAccessRoleDialogConfig>(EditAccessRoleDialog, {
      modal: true,
      draggable: true,
      header: this.translocoService.translate("app.organization-settings.editAccessRoleDialog.header"),
      closable: true,
      width: "40vw",
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { accessRole, organization },
    });

    return dialogRef;
  }

  /**
   * t(app.transactionDetails.approvalDialog.headerApproval)
   * t(app.transactionDetails.approvalDialog.headerRejection)
   */
  public displayTransactionApprovalDialog(
    transactionId: number,
    actionType: TransactionActionType,
    transactionTitle: string,
  ): DynamicDialogRef<TransactionApprovalDialog> {
    const dialogRef = this.dialogService.open<TransactionApprovalDialog, TransactionApprovalDialogConfig>(
      TransactionApprovalDialog,
      {
        modal: true,
        draggable: true,
        header:
          this.translocoService.translate(
            actionType === "approval"
              ? "app.transactionDetails.approvalDialog.headerApproval"
              : "app.transactionDetails.approvalDialog.headerRejection",
          ) +
          " - " +
          transactionTitle,
        closable: true,
        width: "55vw",
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: { transactionId, actionType, transactionTitle },
      },
    );

    return dialogRef;
  }

  public displayOrganizationTosDialog(
    organization: OrganizationDetails,
    canAcceptTos: boolean,
    previewMode = false,
  ): DynamicDialogRef<TosDialog> {
    const dialogRef = this.dialogService.open<TosDialog, TosDialogConfig>(TosDialog, {
      modal: true,
      draggable: true,
      header: this.translocoService.translate("app.tos.dialog.header"),
      closable: true,
      width: "55vw",
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { organization, canAcceptTos, previewMode },
    });

    return dialogRef;
  }

  public displayServiceManageDialog(
    definition: ExtraServiceManageDialogDefinition,
  ): DynamicDialogRef<ServiceManageDialog> {
    const dialogRef = this.dialogService.open<ServiceManageDialog, ExtraServiceManageDialogDefinition>(
      ServiceManageDialog,
      {
        modal: true,
        draggable: true,
        header: definition.title,
        closable: true,
        focusOnShow: false,
        contentStyle: { "overflow-y": "visible" },
        width: "35vw",
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: definition,
      },
    );

    return dialogRef;
  }

  public displayDeactivateExtraServiceDialog(
    serviceTitle: string,
    earliestTerminationDate: Date,
  ): DynamicDialogRef<DeactivateExtraServiceDialog> {
    const dialogRef = this.dialogService.open<DeactivateExtraServiceDialog, DeactivateExtraServiceDialogConfig>(
      DeactivateExtraServiceDialog,
      {
        modal: true,
        draggable: true,
        header: this.translocoService.translate("app.organization-service-details.deactivateDialog.title", {
          serviceTitle,
        }),
        closable: true,
        contentStyle: { "overflow-y": "visible" },
        width: "35vw",
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: { serviceTitle, earliestTerminationDate },
      },
    );

    return dialogRef;
  }

  public displayFormPreviewDialog(fields: FormlyFieldConfig[]): DynamicDialogRef<FormPreviewDialog> {
    const dialogRef = this.dialogService.open<FormPreviewDialog, FormPreviewDialogConfig>(FormPreviewDialog, {
      modal: true,
      draggable: true,
      header: this.translocoService.translate("admin.process-details.formPreview.header"),
      closable: true,
      width: "55vw",
      height: "80vh",
      data: { fields },
    });

    return dialogRef;
  }

  public displayPdfPreviewDialog(fileName: string, pdfBytes: Uint8Array | Blob): DynamicDialogRef<PdfPreviewDialog> {
    const dialogRef = this.dialogService.open<PdfPreviewDialog, PdfPreviewDialogConfig>(PdfPreviewDialog, {
      modal: true,
      draggable: false,
      header: fileName,
      closable: true,
      width: "55vw",
      height: "80vh",
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { fileName, pdfBytes },
    });

    return dialogRef;
  }
}
